import { 
    Typography, 
    Grid, 
    TextField, 
    Box, 
    useTheme, 
    useMediaQuery, 
    IconButton, 
    Card,
    CardContent,
    CardActions,
    Button,
    Divider,
    Skeleton,
    Chip
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { IUser } from "../../redux/models";
import { useState, useCallback } from "react";
import MyToastShow from "../../components/Layout/MyToast";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUpdateFotoMutation, useUpdateUserMutation } from "../../redux/api/userApi";
import jwtDecode from "jwt-decode";
import { LocalStoreManager, LocalStoreKey } from "../../utils/LocalStoreManager";
import { LoadingButton } from "@mui/lab";
import PhotoUpdateModal from "../../components/Profile/PhotoUpdateModal";

const AjustesCuenta = () => {
    const user = useAppSelector((state) => state.userState.user) as IUser;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
    const [updateFoto, { isLoading: isUpdatingPhoto }] = useUpdateFotoMutation();
    const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);

    const handlePhotoUpdate = useCallback(async (formData: FormData) => {
        const res = await updateFoto(formData).unwrap();
        LocalStoreManager.set(LocalStoreKey.LOCALSTORE_TOKEN, res.data.jwtToken);
        const decoded = jwtDecode(res.data.jwtToken);
        dispatch({ type: 'userSlice/setUser', payload: decoded });
        setLastSaved(new Date());
        return res;
    }, [updateFoto, dispatch]);

    const FormItem = ({ label, name, icon, type = 'text', placeholder }: { 
        label: keyof IUser; 
        name: string; 
        icon: React.ReactNode;
        type?: string;
        placeholder?: string;
    }) => {
        const [isEdit, setIsEdit] = useState(false);
        const [editedName, setEditedName] = useState(name);
        const [error, setError] = useState('');

        const validateField = (value: string, fieldName: string) => {
            if (fieldName === 'email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    return 'Por favor ingrese un email válido';
                }
            }
            if (fieldName === 'telefono') {
                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                if (value && !phoneRegex.test(value.replace(/\s/g, ''))) {
                    return 'Por favor ingrese un teléfono válido';
                }
            }
            if (!value.trim() && ['nombre', 'apellido'].includes(fieldName)) {
                return 'Este campo es requerido';
            }
            return '';
        };

        const handleEdit = () => {
            if (isEdit) {
                const validationError = validateField(editedName, label);
                if (validationError) {
                    setError(validationError);
                    return;
                }

                updateUser({
                    id: user.id,
                    [label]: editedName,
                } as unknown as IUser)
                    .unwrap()
                    .then((res) => {
                        MyToastShow({ 
                            message: `${label.charAt(0).toUpperCase() + label.slice(1)} actualizado correctamente`, 
                            type: 'success' 
                        });
                        LocalStoreManager.set(LocalStoreKey.LOCALSTORE_TOKEN, res.data.jwtToken);
                        const decoded = jwtDecode(res.data.jwtToken);
                        dispatch({ type: 'userSlice/setUser', payload: decoded });
                        setLastSaved(new Date());
                        setError('');
                    })
                    .catch((err) => {
                        MyToastShow({ message: 'Error al actualizar la información', type: 'error' });
                        setEditedName(name); // Revert changes
                    })
                    .finally(() => {
                        setIsEdit(false);
                    });
            } else {
                setIsEdit(true);
                setError('');
            }
        };

        const handleCancel = () => {
            setIsEdit(false);
            setEditedName(name);
            setError('');
        };

        return (
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.primary.main + '15',
                                color: theme.palette.primary.main
                            }}
                        >
                            {icon}
                        </Box>
                        <Box flex={1}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {label.charAt(0).toUpperCase() + label.slice(1)}
                            </Typography>
                            {isEdit ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    placeholder={placeholder}
                                    type={type}
                                    error={!!error}
                                    helperText={error}
                                    sx={{ mt: 1 }}
                                />
                            ) : (
                                <Typography variant="body1" fontWeight="medium">
                                    {name || <span style={{ color: theme.palette.text.secondary }}>No especificado</span>}
                                </Typography>
                            )}
                        </Box>
                        {label !== 'email' && (
                            <Box display="flex" gap={1} alignItems="center">
                                {isEdit ? (
                                    <>
                                        <IconButton 
                                            size="small" 
                                            onClick={handleEdit}
                                            disabled={isUpdatingUser}
                                            color="primary"
                                        >
                                            <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            onClick={handleCancel}
                                            disabled={isUpdatingUser}
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <IconButton 
                                        size="small" 
                                        onClick={handleEdit}
                                        disabled={isUpdatingUser}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                    Información de la cuenta
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Gestiona tu información personal y configuración de cuenta
                </Typography>
                {lastSaved && (
                    <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label={`Última actualización: ${lastSaved.toLocaleTimeString()}`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ mt: 1 }}
                    />
                )}
            </Box>

            <Grid container spacing={3}>
                {/* Profile Photo Section */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <Box position="relative" display="inline-block">
                                <img
                                    src={user.fotoPerfil || '/src/assets/user_pic.png'}
                                    alt="Foto de perfil"
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                />
                                <IconButton
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                        },
                                    }}
                                    onClick={() => setOpenPhotoDialog(true)}
                                >
                                    <PhotoCameraIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                {user.nombre} {user.apellido}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {user.empresa || 'Sin empresa especificada'}
                            </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                            <Button
                                startIcon={<CloudUploadIcon />}
                                onClick={() => setOpenPhotoDialog(true)}
                                disabled={isUpdatingPhoto}
                            >
                                Cambiar foto
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* User Information */}
                <Grid item xs={12} md={8}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Información personal
                        </Typography>
                        <FormItem 
                            label="nombre" 
                            name={user.nombre} 
                            icon={<PersonIcon fontSize="small" />}
                            placeholder="Ingrese su nombre"
                        />
                        <FormItem 
                            label="apellido" 
                            name={user.apellido} 
                            icon={<PersonIcon fontSize="small" />}
                            placeholder="Ingrese su apellido"
                        />
                        <FormItem 
                            label="empresa" 
                            name={user.empresa} 
                            icon={<BusinessIcon fontSize="small" />}
                            placeholder="Ingrese el nombre de su empresa"
                        />
                        
                        <Divider sx={{ my: 3 }} />
                        
                        <Typography variant="h6" gutterBottom>
                            Información de contacto
                        </Typography>
                        <FormItem 
                            label="email" 
                            name={user.email} 
                            icon={<EmailIcon fontSize="small" />}
                            type="email"
                        />
                        <FormItem 
                            label="telefono" 
                            name={user.telefono} 
                            icon={<PhoneIcon fontSize="small" />}
                            type="tel"
                            placeholder="Ingrese su número de teléfono"
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Photo Update Modal */}
            <PhotoUpdateModal
                open={openPhotoDialog}
                onClose={() => setOpenPhotoDialog(false)}
                onPhotoUpdate={handlePhotoUpdate}
                isUpdating={isUpdatingPhoto}
                userName={`${user.nombre} ${user.apellido}`}
            />
        </Box>
    );
}

         
export default AjustesCuenta;